/** Element kinds a layer can override. */
export type LayerElementKind = 'node' | 'link' | 'overlay';

export const LAYER_ELEMENT_KINDS: readonly LayerElementKind[] = ['node', 'link', 'overlay'];

/** Layer-only field: false hides the element while the layer is shown. */
export const LAYER_VISIBLE = 'visible';

/** Per-field overrides a layer may hold. Structure (parent, link ends) always comes from base. */
export const LAYER_FIELDS: Record<LayerElementKind, readonly string[]> = {
  node: [
    'name', 'description', 'color', 'type', 'weight', 'x', 'y', 'imageUrl', 'hasBorder', 'properties', 'collapsed',
    LAYER_VISIBLE,
  ],
  link: [
    'name', 'description', 'color', 'type', 'shape', 'direction', 'label', 'weight', 'textFontSize', 'edgeArrowType',
    'labelSlope', 'linkLabelPosition', 'linkLabelOffset', 'bendPointPositions', 'controlPointPositions',
    'cyedgebendeditingDistances', 'cyedgebendeditingWeights', 'cyedgecontroleditingDistances',
    'cyedgecontroleditingWeights', 'sourceEndpoint', 'targetEndpoint', 'sourcePort', 'targetPort', 'amplitude',
    LAYER_VISIBLE,
  ],
  overlay: ['name', 'description', 'x', 'y', 'width', 'height', 'rotation', 'type', 'imageUrl', 'properties', LAYER_VISIBLE],
};

/** Override fields merged key by key, like `properties` on base elements. */
export const LAYER_MERGED_FIELDS: readonly string[] = ['properties'];

export type LayerFields = Record<string, unknown>;

export interface LayerOverrides {
  node: Record<string, LayerFields>;
  link: Record<string, LayerFields>;
  overlay: Record<string, LayerFields>;
}

/** A layer as the change log and the layers endpoint carry it. */
export interface MapLayerRow {
  id: string;
  mapId: string;
  name: string;
  description?: string | null;
  position: number;
  sourceStateId: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MapLayerDto extends MapLayerRow {
  overrides: LayerOverrides;
}

/** One element's overrides in one layer, as a change log row. */
export interface LayerOverrideRow {
  layerId: string;
  kind: LayerElementKind;
  elementId: string;
  fields: LayerFields;
}

export interface MapLayersResponse {
  layers: MapLayerDto[];
}

/** Scene id to layer id, for the scenes that remember a layer. */
export type MapViewLayers = Record<string, string>;

/** A layer as a smaplet scene shows it. */
export interface SceneLayerDto {
  id: string;
  name: string;
  overrides: LayerOverrides;
}

/** Smaplet tester preview only: the layers its scenes show, by scene id. */
export interface SmapletSceneLayers {
  sceneLayers: MapViewLayers;
  layers: SceneLayerDto[];
}

/** Change log row id of one element's overrides in a layer. */
export function layerOverrideRowId(layerId: string, kind: LayerElementKind, elementId: string): string {
  return `${layerId}:${kind}:${elementId}`;
}

export function parseLayerOverrideRowId(id: string): {layerId: string; kind: LayerElementKind; elementId: string} | null {
  const parts = id.split(':');
  if (parts.length !== 3 || LAYER_ELEMENT_KINDS.indexOf(parts[1] as LayerElementKind) < 0) return null;
  return {layerId: parts[0], kind: parts[1] as LayerElementKind, elementId: parts[2]};
}

/** Field key of one override in a layer, next to the element's own field keys. */
export function layerFieldKey(layerId: string, field: string): string {
  return `@layer:${layerId}:${field}`;
}

export function emptyLayerOverrides(): LayerOverrides {
  return {node: {}, link: {}, overlay: {}};
}

export function isLayerField(kind: LayerElementKind, field: string): boolean {
  return LAYER_FIELDS[kind].indexOf(field) >= 0;
}

function stable(value: unknown): string {
  if (value === undefined) return 'null';
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stable).join(',')}]`;
  const record = value as Record<string, unknown>;
  const keys = Object.keys(record).filter((k) => record[k] !== undefined).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stable(record[k])}`).join(',')}}`;
}

/** Deep value equality, treating undefined as null and ignoring key order. */
export function sameLayerValue(a: unknown, b: unknown): boolean {
  return stable(a) === stable(b);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

/** Whether an element is hidden: the layer's `visible` wins, else the base flag. */
export function layerHidden(base: Record<string, unknown>, fields: LayerFields | null | undefined): boolean {
  const visible = fields ? fields[LAYER_VISIBLE] : undefined;
  if (visible === false) return true;
  if (visible === true) return false;
  return !!base['excludedFromState'];
}

/** The element as a layer shows it; `visible` comes back as the `excludedFromState` flag the renderer reads. */
export function resolveWithLayer<T extends Record<string, unknown>>(
  kind: LayerElementKind,
  base: T,
  fields: LayerFields | null | undefined
): T {
  if (!fields) return base;
  const keys = Object.keys(fields);
  if (keys.length === 0) return base;
  const out: Record<string, unknown> = {...base};
  for (const key of keys) {
    if (!isLayerField(kind, key) || key === LAYER_VISIBLE) continue;
    const value = fields[key];
    if (LAYER_MERGED_FIELDS.indexOf(key) >= 0 && isRecord(value)) {
      out[key] = {...(isRecord(base[key]) ? (base[key] as Record<string, unknown>) : {}), ...value};
    } else {
      out[key] = value;
    }
  }
  if (LAYER_VISIBLE in fields) out['excludedFromState'] = layerHidden(base, fields);
  return out as T;
}

/** The overrides that make `base` look like `wanted`. */
export function layerDiff(
  kind: LayerElementKind,
  base: Record<string, unknown>,
  wanted: Record<string, unknown>
): LayerFields {
  const out: LayerFields = {};
  for (const field of LAYER_FIELDS[kind]) {
    if (field === LAYER_VISIBLE) continue;
    if (!(field in wanted)) continue;
    const next = wanted[field];
    if (LAYER_MERGED_FIELDS.indexOf(field) >= 0 && isRecord(next)) {
      const prior = isRecord(base[field]) ? (base[field] as Record<string, unknown>) : {};
      const changed: Record<string, unknown> = {};
      for (const key of Object.keys(next)) {
        if (!sameLayerValue(prior[key], next[key])) changed[key] = next[key];
      }
      if (Object.keys(changed).length) out[field] = changed;
    } else if (!sameLayerValue(base[field], next)) {
      out[field] = next === undefined ? null : next;
    }
  }
  if ('excludedFromState' in wanted) {
    const hidden = !!wanted['excludedFromState'];
    if (hidden !== !!base['excludedFromState']) out[LAYER_VISIBLE] = !hidden;
  }
  return out;
}

const STATE_ENTRY_KEY: Record<LayerElementKind, string> = {node: 'node', link: 'link', overlay: 'element'};

/** The element as a classic v1 state shows it (the entry merged over base), or null when it has no entry. */
export function classicResolved(
  kind: LayerElementKind,
  base: Record<string, unknown>,
  stateId: string
): Record<string, unknown> | null {
  if (kind === 'overlay' && base['excludedFromState']) return null;
  const states = Array.isArray(base['states']) ? (base['states'] as Record<string, unknown>[]) : [];
  let entry: Record<string, unknown> | null = null;
  for (const candidate of states) {
    if (candidate && candidate['stateId'] === stateId) {
      entry = candidate;
      break;
    }
  }
  const data = entry ? entry[STATE_ENTRY_KEY[kind]] : null;
  if (!isRecord(data)) return null;
  const resolved: Record<string, unknown> = {...base};
  for (const key of Object.keys(data)) {
    if (!isLayerField(kind, key) && key !== 'excludedFromState') continue;
    resolved[key] = data[key];
  }
  if (kind === 'overlay' && isRecord(data['properties'])) {
    resolved['properties'] = {...(isRecord(base['properties']) ? base['properties'] : {}), ...data['properties']};
  }
  return resolved;
}

export interface ClassicRows {
  nodes: readonly Record<string, unknown>[];
  links: readonly Record<string, unknown>[];
  overlays: readonly Record<string, unknown>[];
}

/** One element of a classic v1 state as layer overrides, or null when the state leaves it as base. */
export function classicElementOverride(
  kind: LayerElementKind,
  row: Record<string, unknown>,
  stateId: string
): LayerFields | null {
  const resolved = classicResolved(kind, row, stateId);
  if (!resolved) return null;
  const wanted: Record<string, unknown> = {...resolved};
  if (kind === 'overlay') delete wanted['excludedFromState'];
  const fields = layerDiff(kind, row, wanted);
  return Object.keys(fields).length ? fields : null;
}

/** A classic v1 state as layer overrides: whitelisted fields its entries change, plus `visible`. */
export function classicStateOverrides(stateId: string, rows: ClassicRows): LayerOverrides {
  const out = emptyLayerOverrides();
  const take = (kind: LayerElementKind, list: readonly Record<string, unknown>[]) => {
    for (const row of list) {
      const id = typeof row['id'] === 'string' ? (row['id'] as string) : null;
      if (!id) continue;
      const fields = classicElementOverride(kind, row, stateId);
      if (fields) out[kind][id] = fields;
    }
  };
  take('node', rows.nodes);
  take('link', rows.links);
  take('overlay', rows.overlays);
  return out;
}
